import React, { createContext, useContext, useState, useEffect } from 'react';
import bcrypt from 'bcryptjs';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = () => {
      try {
        const sessionData = localStorage.getItem('gameSession');
        if (sessionData) {
          const session = JSON.parse(sessionData);
          // Validate session timestamp (24 hour expiry)
          const sessionAge = Date.now() - session?.timestamp;
          const twentyFourHours = 24 * 60 * 60 * 1000;
          
          if (sessionAge < twentyFourHours) {
            setUser(session?.user);
            setIsAuthenticated(true);
          } else {
            // Session expired
            localStorage.removeItem('gameSession');
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
        localStorage.removeItem('gameSession');
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const register = async (username, email, password, displayName) => {
    try {
      // Get existing users
      const usersData = localStorage.getItem('gameUsers');
      const users = usersData ? JSON.parse(usersData) : [];

      // Check if username already exists
      if (users?.some(u => u?.username === username)) {
        throw new Error('Username already exists');
      }

      // Check if email already exists
      if (users?.some(u => u?.email === email)) {
        throw new Error('Email already registered');
      }

      // Hash password
      const salt = bcrypt?.genSaltSync(10);
      const hashedPassword = bcrypt?.hashSync(password, salt);

      // Create new user
      const newUser = {
        id: Date.now()?.toString(),
        username,
        email,
        displayName,
        password: hashedPassword,
        createdAt: Date.now(),
        stats: {
          totalGems: 0,
          totalWins: 0,
          totalLosses: 0,
          bestStreak: 0,
          currentStreak: 0,
          totalGames: 0,
        },
      };

      // Save to localStorage
      users?.push(newUser);
      localStorage.setItem('gameUsers', JSON.stringify(users));

      // Create session
      const userSession = {
        id: newUser?.id,
        username: newUser?.username,
        displayName: newUser?.displayName,
        email: newUser?.email,
      };

      setUser(userSession);
      setIsAuthenticated(true);

      // Save session
      localStorage.setItem('gameSession', JSON.stringify({
        user: userSession,
        timestamp: Date.now(),
      }));

      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message || 'Registration failed' };
    }
  };

  const login = async (username, password) => {
    try {
      // Get users from localStorage
      const usersData = localStorage.getItem('gameUsers');
      const users = usersData ? JSON.parse(usersData) : [];

      // Find user
      const user = users?.find(u => u?.username === username);
      if (!user) {
        throw new Error('Invalid username or password');
      }

      // Verify password
      const isPasswordValid = bcrypt?.compareSync(password, user?.password);
      if (!isPasswordValid) {
        throw new Error('Invalid username or password');
      }

      // Create session
      const userSession = {
        id: user?.id,
        username: user?.username,
        displayName: user?.displayName,
        email: user?.email,
      };

      setUser(userSession);
      setIsAuthenticated(true);

      // Save session
      localStorage.setItem('gameSession', JSON.stringify({
        user: userSession,
        timestamp: Date.now(),
      }));

      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message || 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('gameSession');
  };

  const updateUserStats = (stats) => {
    if (!user) return;

    try {
      // Update in localStorage
      const usersData = localStorage.getItem('gameUsers');
      const users = usersData ? JSON.parse(usersData) : [];
      const userIndex = users?.findIndex(u => u?.id === user?.id);

      if (userIndex !== -1) {
        users[userIndex].stats = {
          ...users?.[userIndex]?.stats,
          ...stats,
        };
        localStorage.setItem('gameUsers', JSON.stringify(users));
      }
    } catch (error) {
      console.error('Failed to update user stats:', error);
    }
  };

  const getUserStats = () => {
    if (!user) return null;

    try {
      const usersData = localStorage.getItem('gameUsers');
      const users = usersData ? JSON.parse(usersData) : [];
      const userData = users?.find(u => u?.id === user?.id);
      return userData?.stats || null;
    } catch (error) {
      console.error('Failed to get user stats:', error);
      return null;
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateUserStats,
    getUserStats,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
